import { useEffect, useState } from 'react';
import { useAuth } from '../utils/AuthProvider';

import { realtimeDb } from '../firebase';
import ensureFirebaseAuth from '../firebase/ensureFirebaseAuth';
import { presenceEntry } from '../utils/onlinePresence';

/* Presence for the post being edited: /$slugname/$firebaseUid in the
 * `adminonlineusers` Realtime Database.
 *
 * Writing needs a Firebase session, which the CMS only gets by exchanging the
 * admin token for a custom token (ensureFirebaseAuth). Each client owns exactly
 * one child - its own - so two admins on the same post can no longer overwrite
 * each other, and the database rules can keep writes scoped to auth.uid. */

const useOnlineUsers = (slugname) => {
  const [activeUsers, setActiveUsers] = useState({});
  const { user } = useAuth();

  const { email, imageUrl, name } = user || {};

  useEffect(() => {
    // AuthProvider decodes the admin token in its own effect, so the first
    // render has no user yet: waiting for the email keeps a blank entry out of
    // the database (the rules validate it, but the other tabs would render it).
    if (!slugname || !email) return undefined;

    let unmounted = false;
    let dbRef = null;
    let ownRef = null;

    ensureFirebaseAuth(slugname).then((firebaseUser) => {
      if (!firebaseUser || unmounted) return;

      dbRef = realtimeDb.ref(slugname);
      ownRef = dbRef.child(firebaseUser.uid);

      dbRef.on('value', (snapshot) => {
        setActiveUsers(snapshot.val() || {});
      });

      ownRef.onDisconnect().remove();

      ownRef.set(presenceEntry({ email, imageUrl, name }))
        .catch((err) => {
          console.log('Error saving to DB', err);
        });
    });

    return () => {
      unmounted = true;
      if (dbRef) dbRef.off();
      if (ownRef) {
        ownRef.onDisconnect().cancel();
        ownRef.remove()
          .catch((err) => {
            console.log('Error removing from DB', err);
          });
      }
      setActiveUsers({});
    };
  }, [slugname, email, imageUrl, name]);

  return activeUsers;
};

export default useOnlineUsers;
